import { supabase } from './supabase';
import { invokeEdgeFunction } from './invokeEdgeFunction';

/**
 * Per-member monthly call limits. Catalog defaults live on membership_tiers;
 * overrides / unlimited / bonus live on subscriber_memberships.
 */

export type MemberUsageQuota = {
  used: number;
  limit: number | null;
  remaining: number | null;
  unlimited: boolean;
  warn: boolean;
  exhausted: boolean;
  resetsAt: string | null;
  /** Raw edit fields for the usage modal. */
  monthlyCallLimitOverride: number | null;
  usageUnlimited: boolean;
  usageBonusCalls: number;
  usageBonusMonth: string | null;
  tierMonthlyCallLimit: number | null;
  livemode: boolean;
};

type QuotaRpc = {
  used?: number;
  limit?: number | null;
  remaining?: number | null;
  unlimited?: boolean;
  warn?: boolean;
  exhausted?: boolean;
  resets_at?: string | null;
};

function monthStartDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

/** Prefer live membership; fall back to test so staging can still edit. */
export async function pickMembershipLivemode(subscriberId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('subscriber_memberships')
    .select('livemode')
    .eq('subscriber_id', subscriberId);
  if (error) throw new Error(error.message);
  const rows = data ?? [];
  if (rows.some((r) => r.livemode === true)) return true;
  if (rows.some((r) => r.livemode === false)) return false;
  return true;
}

export async function fetchMemberUsageQuota(
  subscriberId: string,
  livemode?: boolean,
): Promise<MemberUsageQuota> {
  const mode = livemode ?? (await pickMembershipLivemode(subscriberId));

  const [{ data: quotaRaw, error: quotaError }, { data: membership, error: memError }] =
    await Promise.all([
      supabase.rpc('mcp_usage_quota', {
        p_subscriber_id: subscriberId,
        p_livemode: mode,
      }),
      supabase
        .from('subscriber_memberships')
        .select(
          'monthly_call_limit_override, usage_unlimited, usage_bonus_calls, usage_bonus_month, tier:membership_tiers(monthly_call_limit)',
        )
        .eq('subscriber_id', subscriberId)
        .eq('livemode', mode)
        .maybeSingle(),
    ]);

  if (quotaError) throw new Error(quotaError.message);
  if (memError) throw new Error(memError.message);

  const quota = (quotaRaw ?? {}) as QuotaRpc;
  const tier = (membership?.tier ?? null) as { monthly_call_limit?: number | null } | null;

  return {
    used: Number(quota.used ?? 0),
    limit: quota.limit == null ? null : Number(quota.limit),
    remaining: quota.remaining == null ? null : Number(quota.remaining),
    unlimited: Boolean(quota.unlimited),
    warn: Boolean(quota.warn),
    exhausted: Boolean(quota.exhausted),
    resetsAt: typeof quota.resets_at === 'string' ? quota.resets_at : null,
    monthlyCallLimitOverride:
      membership?.monthly_call_limit_override == null
        ? null
        : Number(membership.monthly_call_limit_override),
    usageUnlimited: Boolean(membership?.usage_unlimited),
    usageBonusCalls: Number(membership?.usage_bonus_calls ?? 0),
    usageBonusMonth:
      typeof membership?.usage_bonus_month === 'string' ? membership.usage_bonus_month : null,
    tierMonthlyCallLimit:
      tier?.monthly_call_limit == null ? null : Number(tier.monthly_call_limit),
    livemode: mode,
  };
}

export type UsageLimitEdit = {
  monthlyCallLimitOverride: number | null;
  usageUnlimited: boolean;
  /** Extra calls to credit this calendar month. 0 clears the bonus. */
  creditCalls: number;
};

export async function saveMemberUsageLimits(
  subscriberId: string,
  _livemode: boolean,
  edit: UsageLimitEdit,
): Promise<void> {
  const { error: ensureError } = await supabase.rpc('ensure_free_membership_rows', {
    p_subscriber_id: subscriberId,
  });
  if (ensureError) throw new Error(ensureError.message);

  const bonusCalls = Math.max(0, Math.floor(edit.creditCalls));
  const payload: Record<string, unknown> = {
    monthly_call_limit_override: edit.usageUnlimited
      ? null
      : edit.monthlyCallLimitOverride,
    usage_unlimited: edit.usageUnlimited,
    usage_bonus_calls: bonusCalls,
    usage_bonus_month: bonusCalls > 0 ? monthStartDate() : null,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('subscriber_memberships')
    .update(payload)
    .eq('subscriber_id', subscriberId);

  if (error) throw new Error(error.message);
}

export type UsageNoteKind = 'credit' | 'exhausted';

export function defaultUsageNote(
  kind: UsageNoteKind,
  opts: { creditCalls?: number; firstName?: string | null },
): { subject: string; body: string } {
  const name = opts.firstName?.trim() || 'there';
  if (kind === 'credit') {
    const n = opts.creditCalls ?? 0;
    return {
      subject: 'A few more My AI CTO calls this month',
      body: `Hey ${name},\n\nWe love your usage — I've credited you ${n} more calls this month to keep the magic flowing.`,
    };
  }
  return {
    subject: "You're out of My AI CTO calls this month",
    body: `Hey ${name},\n\nIt looks like you're out of calls this month. Upgrade for a higher limit so your assistant can keep working.`,
  };
}

export async function sendUsageNote(input: {
  subscriberId: string;
  subject: string;
  personalMessage: string;
  includeUpgradeCta: boolean;
}): Promise<void> {
  await invokeEdgeFunction('llc-send-usage-note', {
    subscriber_id: input.subscriberId,
    subject: input.subject,
    personal_message: input.personalMessage,
    include_upgrade_cta: input.includeUpgradeCta,
  });
}
